import { Button, Modal, message } from 'antd';
import React, { useState } from 'react';
import DataProductBuilderForm from './DataProductBuilderForm';
import { DataProductBuilderState } from './types';
import { useCreateDataProductMutation } from '../../../../graphql/dataProduct.generated';
import { DataProduct, Domain } from '../../../../types.generated';
import { useTranslation } from '../../../../i18n/useTranslation';

export const MODAL_WIDTH = '75vw';

export const MODAL_BODY_STYLE = {
    overflow: 'auto',
    width: '80vw',
    maxWidth: 800,
};

const DEFAULT_STATE = {
    name: '',
};

type Props = {
    domain?: Domain;
    onClose: () => void;
    onCreateDataProduct: (dataProduct: DataProduct) => void;
    refetch?: () => void;
};

export default function CreateDataProductModal({ domain, onCreateDataProduct, onClose, refetch }: Props) {
    const { t } = useTranslation();
    const [builderState, updateBuilderState] = useState<DataProductBuilderState>({
        ...DEFAULT_STATE,
        domain: domain ?? undefined,
    });
    const [createDataProductMutation, { loading }] = useCreateDataProductMutation();

    function createDataProduct() {
        createDataProductMutation({
            variables: {
                input: {
                    domainUrn: builderState.domain?.urn ?? '',
                    properties: {
                        name: builderState.name.trim(),
                        description: builderState.description || undefined,
                    },
                },
            },
        })
            .then(({ data, errors }) => {
                if (!errors) {
                    message.success(t('Created Data Product!'));
                    if (data?.createDataProduct) {
                        const updateDataProduct = { ...data.createDataProduct, domain: { domain } };
                        onCreateDataProduct(updateDataProduct as DataProduct);
                    }
                    refetch?.();
                    // Only close on success so a failure (e.g. a duplicate name) keeps the modal
                    // open with the entered values for the user to correct and resubmit.
                    onClose();
                }
            })
            .catch((e) => {
                message.destroy();
                message.error({ content: `Failed to create Data Product. \n ${e.message || ''}`, duration: 3 });
            });
    }

    return (
        <Modal
            title={<h1>{t('Create new Data Product')}</h1>}
            className="f-modal"
            onCancel={onClose}
            // style={MODAL_BODY_STYLE}
            width={MODAL_WIDTH}
            centered
            open
            footer={
                <>
                    <Button onClick={onClose} type="default">
                        {t('Cancel')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={createDataProduct}
                        disabled={!builderState.name || !builderState.domain || loading}
                    >
                        {t('Create')}
                    </Button>
                </>
            }
        >
            <DataProductBuilderForm
                builderState={builderState}
                updateBuilderState={updateBuilderState}
                showDomain={domain === undefined}
            />
        </Modal>
    );
}
